import { FC, useState, useEffect, useContext } from "react";
import { patchPatientMilestone } from "../../api/endpoints";
import { useMutation, useQueryClient } from "react-query";
import Checkbox from "@mui/material/Checkbox";
import { MilestoneStatusChoices } from "../../types/MilestoneStatusChoices";
import type { AxiosError } from "axios";
import APIContext from "../../contexts/APIContext";
import { UI_ACCENT_BLUE } from "../../constants/ui-colors";

interface MilestoneItemsProp {
  data: any;
  disabled: boolean;
  isPending?: boolean;
}

const MilestoneItem: FC<MilestoneItemsProp> = ({
  data,
  disabled,
  isPending = false,
}) => {
  const [checked, setChecked] = useState(false);
  const { setShowErrorToast } = useContext(APIContext);
  const queryClient = useQueryClient();

  // Disable if: mutation pending OR no real ID yet (optimistic update)
  const isNotReady = isPending || !data?.id;

  const sxStyles = {
    checkbox: {
      color: UI_ACCENT_BLUE,
      padding: 0,
      margin: 0,
      alignSelf: "flex-start",
      "&:hover, &.Mui-focusVisible": {
        backgroundColor: "transparent !important",
      },
      "&.Mui-checked": {
        color: UI_ACCENT_BLUE,
      },
    },
  };

  useEffect(() => {
    if (data) {
      setChecked(
        [
          MilestoneStatusChoices.COMPLETED,
          MilestoneStatusChoices.ALWAYS_NORMAL,
        ].includes(data.milestone_status)
      );
    }
  }, [data]);

  const patientMilestoneMutation = useMutation(patchPatientMilestone, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["patientmilestones", data.patient],
      });
    },
    onError: (error: AxiosError) => {
      // revert the change if the mutation fails
      if (data)
        setChecked(
          [
            MilestoneStatusChoices.COMPLETED,
            MilestoneStatusChoices.ALWAYS_NORMAL,
          ].includes(data.milestone_status)
        );
      setShowErrorToast(true);
    },
  });

  const handleUpdate = (event) => {
    const payload = {
      id: event.target.value,
      milestone_status: event.target.checked
        ? MilestoneStatusChoices.COMPLETED
        : MilestoneStatusChoices.INCOMPLETE,
    };
    setChecked(event.target.checked);
    patientMilestoneMutation.mutate(payload);
  };

  return (
    <Checkbox
      value={`${data.id}`}
      size="small"
      sx={sxStyles.checkbox}
      checked={checked}
      onChange={handleUpdate}
      disabled={disabled || isNotReady}
    />
  );
};

export default MilestoneItem;
